import { useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { COMMUNE_NOM_DEFAUT, ETAPES, ETAPE_INFO } from '@dinertournant/core';
import { BandeauAlerte, IconeCommune, PastilleEtape } from '../../components/index.js';
import { capitale, dateLongue, heureCourte, joursAvant, type Evenement } from '../../lib/api.js';
import type { ContexteEvenement } from './EvenementLayout.js';
import { EvenementForm } from './EvenementForm.js';

/** Étape 1 de la barre latérale : récapitulatif de l'événement, lien d'inscription, modification. */
export function EvenementPage() {
  const { detail, recharger } = useOutletContext<ContexteEvenement>();
  // `?modifier` ouvre directement le formulaire (lien depuis d'autres écrans).
  const [params] = useSearchParams();
  const [edition, setEdition] = useState(params.has('modifier'));
  const [copie, setCopie] = useState(false);
  const e = detail.event;
  const jours = joursAvant(e.dateLimiteInscription);
  const horaires = { entree: e.horaireEntree, plat: e.horairePlat, dessert: e.horaireDessert };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(detail.liens.inscription);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch {
      window.prompt('Copiez le lien :', detail.liens.inscription);
    }
  };

  const onEnregistre = async (_ev: Evenement) => {
    setEdition(false);
    await recharger();
  };

  if (edition) {
    return (
      <div className="page-app" style={{ maxWidth: 760 }}>
        <div className="entete-page">
          <h1 className="display">Modifier l'événement</h1>
        </div>
        <EvenementForm evenement={e} onEnregistre={onEnregistre} />
        <button type="button" className="btn" onClick={() => setEdition(false)} style={{ alignSelf: 'flex-start' }}>
          Annuler
        </button>
      </div>
    );
  }

  return (
    <div className="page-app">
      <div className="entete-page">
        <div>
          {e.organisation ? <div className="surtitre">{e.organisation}</div> : null}
          <h1 className="display">{e.nom}</h1>
          <p className="texte-doux">
            {capitale(dateLongue(e.date))}
            {e.zone ? ` · ${e.zone}` : ''}
          </p>
        </div>
        <div className="ligne">
          <button type="button" className="btn" onClick={() => setEdition(true)}>
            Modifier
          </button>
          <Link to={`/app/e/${e.id}/participants`} className="btn principal">
            Participants →
          </Link>
        </div>
      </div>

      <div className="cartes-stats">
        <div className="carte stat">
          <span className="valeur">{detail.inscriptions.participants}</span>
          <span className="libelle">participants présents</span>
          <span className="detail">
            {detail.inscriptions.foyers}  voiture{detail.inscriptions.foyers > 1 ? 's' : ''}
            {e.capaciteMax ? ` · ${detail.inscriptions.participants} / ${e.capaciteMax} places` : ''}
          </span>
        </div>
        <div className="carte stat">
          <span className="valeur">{jours >= 0 ? jours : 0}</span>
          <span className="libelle">{jours >= 0 ? 'jours avant la clôture' : 'inscriptions closes'}</span>
          <span className="detail">fin des inscriptions le {dateLongue(e.dateLimiteInscription)}</span>
        </div>
        <div className="carte pile" style={{ gap: 8 }}>
          <span className="libelle" style={{ fontWeight: 700 }}>
            {e.communeActive && e.communeHeure ? 'Les quatre étapes' : 'Les trois étapes'}
          </span>
          {ETAPES.map((et) => (
            <div key={et} className="ligne" style={{ fontSize: 15 }}>
              <PastilleEtape etape={et} variante="numero" />
              <span style={{ marginLeft: 'auto' }}>{heureCourte(horaires[et])}</span>
            </div>
          ))}
          {e.communeActive && e.communeHeure ? (
            <div className="ligne" style={{ fontSize: 15 }}>
              <span className="pastille etape-commune">
                <span className="icone">
                  <IconeCommune />
                </span>
                <span className="texte">
                  <span className="libelle">{e.communeNom?.trim() || COMMUNE_NOM_DEFAUT}</span>
                  <span className="detail">{e.communeLieu}</span>
                </span>
              </span>
              <span style={{ marginLeft: 'auto' }}>{heureCourte(e.communeHeure)}</span>
            </div>
          ) : null}
        </div>
      </div>

      <section className="carte pile" aria-labelledby="lien">
        <h2 id="lien" style={{ fontSize: 18 }}>
          Lien d'inscription
        </h2>
        <p className="texte-doux">Partagez ce lien aux Lions : chacun s'inscrit depuis son téléphone, avec ou sans proposition de recevoir.</p>
        <div className="ligne lien-inscription">
          <code>{detail.liens.inscription}</code>
          <button type="button" className="btn" onClick={copier} aria-live="polite">
            {copie ? 'Copié !' : 'Copier le lien'}
          </button>
          <a className="btn" href={detail.liens.inscription} target="_blank" rel="noreferrer">
            Ouvrir
          </a>
        </div>
      </section>

      {e.revealAdresseMinAvant !== null ? (
        <BandeauAlerte niveau="info" titre="Adresses surprises activées.">
          Chaque adresse est dévoilée {e.revealAdresseMinAvant} min avant l'étape, uniquement aux convives de la table.
        </BandeauAlerte>
      ) : null}
      {detail.planning.planningObsolete ? (
        <BandeauAlerte niveau="sombre" titre={`${detail.planning.changements.length} changement${detail.planning.changements.length > 1 ? 's' : ''} depuis la répartition.`}>
          Les parcours déjà envoyés restent valables tant que vous ne régénérez pas. <Link to={`/app/e/${e.id}/repartition`} style={{ color: 'inherit' }}>Voir la répartition</Link>
        </BandeauAlerte>
      ) : null}
      <p className="texte-muet" style={{ fontSize: 13 }}>
        {ETAPE_INFO.entree.label} chez un premier hôte, {ETAPE_INFO.plat.label.toLowerCase()} chez un deuxième, {ETAPE_INFO.dessert.label.toLowerCase()} chez un troisième : tous les participants prennent part aux trois étapes.
      </p>
    </div>
  );
}
